/*
 * Publication targets — registry of where aggregate-domain data can be
 * pushed: DHIS2 servers, CSV exports, external APIs. Read-mostly view
 * over the existing /api/aggregate-publication/profiles. Authoring full
 * profiles (with auth modes, domain bindings, etc.) lives on the
 * aggregate-domain page; this surface is the cross-workspace inventory
 * with enable/disable + delete actions.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import TableToolbar from "../../components/TableToolbar.js";
import { useTableState } from "../../lib/useTableState.js";
import { api, ApiError, AggregatePublicationProfile } from "../../lib/api.js";

export default function AdminPublicationTargets() {
  const [profiles, setProfiles] = useState<AggregatePublicationProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function refresh() {
    setError(null);
    api.aggregatePublicationProfiles()
      .then((r) => setProfiles(r.profiles))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }
  useEffect(refresh, []);

  async function toggleEnabled(p: AggregatePublicationProfile) {
    setBusyId(p.id);
    setInfo(null);
    setError(null);
    try {
      await api.upsertAggregatePublicationProfile({ ...p, enabled: !p.enabled });
      setInfo(`${p.name} ${p.enabled ? "disabled" : "enabled"}.`);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(p: AggregatePublicationProfile) {
    if (!window.confirm(`Delete publication target "${p.name}"? Existing runs are preserved.`)) return;
    setBusyId(p.id);
    setInfo(null);
    setError(null);
    try {
      await api.deleteAggregatePublicationProfile(p.id);
      setInfo(`${p.name} deleted.`);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const t = useTableState((profiles ?? []) as unknown as Array<Record<string, unknown>>, {
    initialPageSize: 25,
    searchFields: ["id", "name", "endpoint", "targetType", "authMode"],
    initialSortKey: "name"
  });

  const stats = useMemo(() => {
    const list = profiles ?? [];
    return {
      total: list.length,
      enabled: list.filter((p) => p.enabled).length,
      dhis2: list.filter((p) => p.targetType === "dhis2").length,
      external: list.filter((p) => p.targetType === "external-api").length,
      csv: list.filter((p) => p.targetType === "csv-export").length
    };
  }, [profiles]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Publication targets</h1>
          <p className="muted">
            Registered destinations for aggregate-domain publications. Each profile binds a set of
            domains to a specific endpoint (a DHIS2 server, a CSV export bucket, or an external
            ingest API). Targets can be temporarily disabled without deleting their run history.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {info && <div className="ts-alert ts-alert-info">{info}</div>}

      <section className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
          <Stat label="Targets" value={stats.total} />
          <Stat label="Enabled" value={stats.enabled} accent="ok" />
          <Stat label="DHIS2" value={stats.dhis2} />
          <Stat label="External API" value={stats.external} />
          <Stat label="CSV export" value={stats.csv} />
        </div>
      </section>

      {profiles === null ? (
        <p className="muted">Loading…</p>
      ) : profiles.length === 0 ? (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No publication targets configured</h2>
          <p>
            Author a publication profile from an aggregate domain's detail drawer, or create one
            via <code>POST /api/aggregate-publication/profiles</code>. Once a target exists,
            scheduled or manual publication runs become possible.
          </p>
        </div>
      ) : (
        <>
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            totalPages={t.totalPages} totalRows={t.totalRows}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            searchPlaceholder="name / endpoint / type…"
            exportFilename="publication-targets"
            exportColumns={["id", "name", "targetType", "endpoint", "authMode", "enabled", "domainIds"]}
            exportRowsData={t.sorted}
          />
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Endpoint</th><th>Auth</th>
                <th style={{ textAlign: "right" }}>Domains</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as AggregatePublicationProfile[]).map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    <span className="pill pill-info">{p.targetType}</span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, wordBreak: "break-all", maxWidth: 360 }}>{p.endpoint}</td>
                  <td>{p.authMode}</td>
                  <td style={{ textAlign: "right" }}>{p.domainIds.length}</td>
                  <td>
                    <span className={`pill ${p.enabled ? "pill-ok" : "pill-muted"}`}>
                      {p.enabled ? "enabled" : "disabled"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void toggleEnabled(p)}
                      disabled={busyId === p.id}
                      style={{ marginRight: 6 }}
                    >
                      {p.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void remove(p)}
                      disabled={busyId === p.id}
                      style={{ color: "#7f1d1d" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "ok" }) {
  const color = accent === "ok" ? "#065f46" : "#1e293b";
  const bg = accent === "ok" ? "#d1fae5" : "#f1f5f9";
  return (
    <div style={{ background: bg, color, padding: "10px 12px", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value.toLocaleString()}</div>
    </div>
  );
}
